import ExpiryMap from "expiry-map";
import { Nullable } from "types/app";
import API from "Classes/Api";
import { ApiGetCallParams, ApiPostCallParams } from "Classes/ApiCallParams";
import {
  ALERT_PRIORITIES,
  CLIENT_STATUSES,
  DAYS,
  FD_CLIENT,
  PLATFORMS,
  SHORTEN_PLATFORMS,
  SilencingWindowsState,
  TIMES,
  TIME_FRAMES,
} from "Consts";
import { store } from "reduxStore/store";
import {
  sleep,
  sortListByProperty,
  timeZoneOffset,
  getUniqueSortedList,
  sortTimezonesByUtcOffset,
  isNullOrUndefined,
} from "utils";
import { getRandomString } from "../utils/stringUtils";
import EnvironmentService from "./Dashboards/Environment_V2";

import { GetResourceFiltersRequestBody, GetResourceFiltersResponse } from "./ServicesTypes/Lookup";

const CacheTimeMS = 60_000 * 30; // 1 minute * 30 = 30 minutes

const TASK_STATUS = {
  data: [
    { display: "Success", id: 1, name: "1" },
    { display: "Failure", id: 0, name: "0" },
  ],
};
const TASK_ORIGINS = {
  data: [
    { display: "User", id: 0, name: "0" },
    { display: "System", id: 1, name: "1" },
  ],
};
const TASK_CONFIG_STATUS = {
  data: [
    { display: "None", id: 0, name: "0" },
    { display: "Partial", id: 1, name: "1" },
    { display: "Complete", id: 1, name: "2" },
  ],
};
const SETUP_STATUS = {
  data: [
    { id: 0, name: "Complete" },
    { id: 0, name: "Incomplete" },
  ],
};

const TASK_CONFIGURATION = {
  data: [
    { display: "Not Configured", id: 0, name: "0" },
    { display: "All Successful", id: 1, name: "1" },
    { display: "Partially Successful", id: 1, name: "2" },
    { display: "All Failed", id: 1, name: "3" },
  ],
};
const PROJECT_STATUS = {
  data: [
    { id: 0, name: "Inactive" },
    { id: 1, name: "Active" },
  ],
};

const Cloud_Provider = [
  { name: "AWS", value: 1 },
  { name: "Azure", value: 2 },
  { name: "GCP", value: 3 },
];

const LookupsMap = new ExpiryMap(CacheTimeMS); // clear on screen refresh, or after [CacheTimeMS]
const InstanceCacheKeys = [
  "database_instances_list_dashboard",
  "sql_instances",
  "availability_groups_instances",
  "availability_groups_instance_databases",
];

const HostCacheKeys: any = [];
class LookupService extends API {
  inProgressPaths: Map<any, any>;
  id: string;
  constructor() {
    super();
    this.inProgressPaths = new Map();
    this.id = getRandomString(15);
  }

  async getResourceFilters(
    clientId: number,
    body: GetResourceFiltersRequestBody
  ): Promise<GetResourceFiltersResponse> {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/resources/filters`,
        headers: { ...API.getAuthHeaders() },
        body,
        failureMessage: "Failed to get resource filters",
      })
    );

    return (
      result || { serverFilters: [], hostFilters: [], instanceFilters: [], databaseFilters: [] }
    );
  }

  getPromiseMyFDType(fdType: string, clientId: number, predicate: any = null) {
    const state = store.getState();
    const {
      auth: { allowedClients },
      globalFilter: { GlobalTopFilterObject },
    }: any = state;

    switch (fdType) {
      case "cloud_providers":
        return new Promise((resolve) => {
          const tz = {
            data: Cloud_Provider,
          };
          resolve(tz);
        });

      case "silencing_window_states":
        return new Promise((resolve) => {
          const tz = {
            data: SilencingWindowsState.map((obj) => {
              return { display: obj.name, name: obj.id };
            }),
          };
          resolve(tz);
        });

      case "timezones":
        return this.getTimezones();

      case "times": // hours 0-23
        return new Promise((resolve) => {
          resolve({
            data: Object.entries(TIMES).map(([index, obj]) => {
              return { id: index, name: obj.name };
            }),
          });
        });

      case "time_frames":
        return new Promise((resolve) => {
          resolve({
            data: TIME_FRAMES.map((obj) => {
              return { display: obj.name, name: obj.value, value: obj.value };
            }),
          });
        });

      case "instance_platforms": //todo: replace platforms below with this one
      case "instancesPlatform": {
        return new Promise((resolve) => {
          this.getEnumeration("platforms").then((res) => {
            resolve({
              data: res.data
                .filter((p: any) => p.category === "Instances")
                .map((p: any) => ({ name: p.platform, value: p.platformID })),
            });
          });
        });
        //todo: replace platforms below with this one
      }

      case "platforms":
        return new Promise((resolve) => {
          resolve({
            data: PLATFORMS.map((platform) => {
              return { id: platform.id, name: platform.name };
            }),
          });
        });

      case "shorten_platforms":
        return new Promise((resolve) => {
          resolve({
            data: SHORTEN_PLATFORMS,
          });
        });

      case "alert_priorities":
        return new Promise((resolve) => {
          resolve({
            data: ALERT_PRIORITIES.map((priority) => {
              return { id: priority, name: priority };
            }),
          });
        });

      case "aggregations":
        return new Promise((resolve) => {
          resolve({
            data: [
              { id: 1, name: "Average" },
              { id: 2, name: "Max" },
              { id: 3, name: "Min" },
              { id: 4, name: "Median" },
              { id: 5, name: "Percentile 95%" },
              { id: 6, name: "Percentile 99%" },
            ],
          });
        });

      case "days":
        return new Promise((resolve) => {
          resolve({
            data: Object.entries(DAYS).map(([, obj]) => {
              return { display: obj.name, name: obj.name, value: obj.name };
            }),
          });
        });

      case "roles":
        return this.getEnumeration("team_member_organizational_roles").then();

      case "monitoringTypes":
        return this.getMonitoringTypes();

      case "monitoring_alert_types":
        return this.getEnumeration("monitoring_alert_types");

      case "environmentTypes":
        return this.getEnvironmentTypes();

      case "os":
        return this.getOperatingSystemTypes();

      case "projectStatusTypes":
        return this.getProjectStatusTypes();

      case "instance_task_configuration_status":
        return this.getInstanceTaskConfiguration();

      case "applicationTiers":
        return this.getApplicationTiers();

      case "tierOfService":
        return this.getTiersOfService();

      case "team_member_profiles":
        return this.getEnumeration("team_member_profiles");

      case "team_member_organizational_roles":
        return this.getEnumeration("team_member_organizational_roles");

      case "applicationTypes":
        return this.getApplicationTypes();

      case "loginTypes":
        return this.getLoginTypes();

      case "serverTypes":
        return this.getServerTypes();

      case "collectors_v2":
        return this.getCollectorsV2(clientId);

      case "clients": {
        return new Promise((resolve) => {
          resolve({
            data: sortListByProperty(
              allowedClients?.map(
                (client: any) =>
                  // @ts-ignore
                  ({
                    name: client.name,
                    value: client.clientID,
                  }) ?? []
              ) ?? [],
              "name",
              "asc"
            ),
          });
        });
      }

      case "collectorEnvironments":
        return EnvironmentService.getCollectorEnvironments(clientId).then((result) => {
          return {
            data: [
              { display: "[Unassigned]", name: "[Unassigned]", value: "[Unassigned]", id: "0" },
              ...((result && result.environments) || []).map((i) => ({
                // ...i,
                display: i.name,
                name: i.name,
                value: i.name,
                id: i.id.toString(),
              })),
            ].sort((a, b) => (a.name > b.name ? 1 : -1)),
          };
        });

      case "task_types":
        if (!clientId) return Promise.resolve();
        return this.getEnumeration("maintenance/task_types", clientId);

      case "task_status":
        return Promise.resolve(TASK_STATUS);

      case "task_origin":
        return Promise.resolve(TASK_ORIGINS);

      case "task_configuration_status":
        return Promise.resolve(TASK_CONFIG_STATUS);

      case "usStates":
        return this.getUsStates();

      case "userRoles":
        return this.getRoles();
      case "clientStatuses":
        return new Promise((resolve) => {
          resolve({
            data: CLIENT_STATUSES,
          });
        });

      case "setupStatus":
        return Promise.resolve(SETUP_STATUS);

      case "contacts": // contacts are client's users
      case "users":
        return this.getUsers(clientId);

      case "usersWithUnassigned":
        return this.getOpportunitiesOwners(clientId);

      case "findingOwners":
        return this.getFindingOwners(clientId);

      case "impactTypes":
        return this.getImpactTypes();

      case "queryObservationTypes":
        return this.getQueryObservationTypes();

      case "yesno":
        return new Promise((resolve) => {
          resolve({
            data: [
              {
                name: "Yes",
                value: "Yes",
              },
              {
                name: "No",
                value: "No",
              },
            ],
          });
        });
      case "job_statuses":
        return new Promise((resolve) => {
          resolve({
            data: [
              {
                display: "Enabled",
                name: "1",
              },
              {
                display: "Disabled",
                name: "0",
              },
            ],
          });
        });
      case "clientManagementTypes":
        return this.getClientManagementTypes();

      case "clientSubscriptionStatuses":
        return this.getClientSubscriptionStatuses();

      case "servers":
        return this.getServers(clientId);

      case "programs":
        return this.getProgramsNames(clientId);

      case "login_names":
        return this.getLoginNames(clientId);

      case "waitStats":
        return this.getWaitStats(clientId);

      case "fdUsers":
        return this.getUsers(FD_CLIENT); // users of fortified

      case "connections_instance_hosts_by_instance":
        return this.getEnumeration("connections_instance_hosts_by_instance", clientId, {
          instance: "",
        }).then((res) => {
          return {
            data: ((res && res.data) || []).map((u: any) => ({
              display: u.host_name,
              name: u.host_name,
            })),
          };
        });

      case "login_by_instance_async":
        return this.getInstanceLoginsList(clientId).then((result) => {
          if (!result || !result.data) Promise.resolve([]);
          return { data: predicate ? predicate(result.data) : result.data };
        });

      case "program_by_instance":
        return this.getProgramsNames(clientId).then((result) => {
          if (!result || !result.data) Promise.resolve([]);
          return { data: predicate ? predicate(result.data) : result.data };
        });
      case "authenticationMethods": {
        return this.getAuthenticationMethods().then((result) => {
          return {
            data: ((result && result.data) || [])
              .map((u: any) => ({
                ...u,
                id: u.authenticationMethodID,
                name: u.authenticationMethod,
              }))
              .sort((a: any, b: any) => (a.name > b.name ? 1 : -1)),
          };
        });
      }

      case "server_management_types":
        return this.getServerManagementTypes();

      case "sql_audit_object_types":
        return this.getEnumeration("sql_audit_object_types").then((result) => {
          return {
            data: ((result && result.data) || [])
              .map((u: any) => ({
                ...u,
                display: u.name,
                name: u.name,
                id: u.id?.trim(),
              }))
              .sort((a: any, b: any) => a?.name?.localeCompare(b?.name)),
          };
        });

      case "sql_audit_event_types":
        return this.getEnumeration("sql_audit_event_types").then((result) => {
          return {
            data: ((result && result.data) || [])
              .map((u: any) => ({
                ...u,
                display: u.name,
                name: u.name,
                id: u.id?.trim(),
              }))
              .sort((a: any, b: any) => a?.name?.localeCompare(b?.name)),
          };
        });

      case "availability_groups_instances":
        return this.getAvailabilityGroupInstancesList(clientId);

      case "availability_groups_instance_databases":
        return this.getAvailabilityGroupDatabaseList(clientId).then((result: any) => {
          if (!result || !result.data) Promise.resolve([]);
          return { data: predicate ? predicate(result.data) : result.data };
        });

      case "finding_statuses":
        return this.getEnumeration("finding_statuses");

      case "plan_types":
        return this.getEnumeration("plan_types");

      case "msp_no_direct":
        return this.getEnumeration("msp").then((resp) => ({
          data: resp.data.filter((item: any) => item.id !== 0),
        }));

      case "msp":
        return this.getEnumeration("msp");

      case "platform":
        return this.getPlatforms();

      case "servers_v2":
        return this.getServersV2(clientId);

      case "hosts_v2":
        return this.getHostsV2(clientId);

      case "instances_v2":
        return this.getInstancesV2(clientId);

      case "databases_v2":
        return this.getDatabasesV2(clientId);

      case "resources_v2":
        return this.getResourcesV2F(clientId);

      case "database_editions_v2":
        return this.getDatabaseEditionV2();

      case "resource_tags_v2":
        return this.getTagsV2(clientId, null);

      case "alert_types_v2":
        return this.getAlertTypes(clientId);

      case "alert_severity_type":
        return this.getAlertSeverityTypes(clientId);

      case "alert_category_type":
        return this.getAlertCategoryTypes(clientId);

      case "perfmon_agg":
        return this.getPerfmonAggV2();

      case "optimizationTypes":
        return this.getOptimizationTypes();

      case "process_v2":
        return this.getProcess(clientId, GlobalTopFilterObject?.servername || []);

      case "program_v2":
        return this.getProgram(clientId, GlobalTopFilterObject?.servername || []);

      case "manufacturer_v2":
        return this.getManufacturer(clientId, GlobalTopFilterObject?.servername || []);

      case "category_v2":
        return this.getCategory(clientId, GlobalTopFilterObject?.servername || []);

      case "business_goal_types":
        return this.getBusinessGoalTypes();

      case "effort_types":
        return this.getEffortTypes();

      default:
        return this.getEnumeration(fdType);
    }
  }

  async getProcess(clientId: any, hosts: any) {
    const url =
      hosts?.length > 0
        ? `v2/clients/${clientId}/processes/lookup?${hosts?.map((host: any) => `hostName=${host}`)?.join("&")}`
        : `v2/clients/${clientId}/processes/lookup`;
    const result = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        result?.processes?.map((i: any) => ({
          ...i,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  async getProgram(clientId: any, hosts: any) {
    const url =
      hosts?.length > 0
        ? `v2/clients/${clientId}/processes/programs/lookup?${hosts?.map((host: any) => `hostName=${host}`)?.join("&")}`
        : `v2/clients/${clientId}/processes/programs/lookup`;
    const result = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        result?.processPrograms?.map((i: any) => ({
          ...i,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  async getManufacturer(clientId: any, hosts: any) {
    const url =
      hosts?.length > 0
        ? `v2/clients/${clientId}/processes/manufacturers/lookup?${hosts?.map((host: any) => `hostName=${host}`)?.join("&")}`
        : `v2/clients/${clientId}/processes/manufacturers/lookup`;

    const result = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        result?.processManufacturers?.map((i: any) => ({
          ...i,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  async getCategory(clientId: any, hosts: any) {
    const url =
      hosts?.length > 0
        ? `v2/clients/${clientId}/processes/categories/lookup?${hosts?.map((host: any) => `hostName=${host}`)?.join("&")}`
        : `v2/clients/${clientId}/processes/categories/lookup`;

    const result = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        result?.processCategories?.map((i: any) => ({
          ...i,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  getProgramsListByInstance(elasticDBSearchParams: any) {
    if (!elasticDBSearchParams.clientId) return Promise.resolve([]);
    return super.call(
      new ApiPostCallParams({
        action: `${elasticDBSearchParams.clientId}/programs_by_instance`,
        body: {
          data: {
            ...elasticDBSearchParams,
          },
        },
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getUsers(clientId: number) {
    const result = await this.getEnumeration("user_list", clientId);
    const users = sortListByProperty(result?.data ?? [], "name", "asc");
    return {
      data: users,
    };
  }

  async getUsersWithUnassigned(clientId: number) {
    const result = await this.getEnumeration("user_list", clientId);
    const users = sortListByProperty(result?.data ?? [], "name", "asc");
    return {
      data: [{ id: 0, name: "Unassigned" }, ...users],
    };
  }

  getServerManagementTypes() {
    return this.getEnumeration("server_management_types");
  }

  getApplicationTiers() {
    return this.getEnumeration("application_tiers");
  }

  getTiersOfService() {
    return this.getEnumeration("application_tiers");
  }

  getApplicationTypes() {
    return this.getEnumeration("application_types");
  }

  getLoginTypes() {
    return this.getEnumeration("login_types");
  }

  getEnvironmentTypes() {
    return this.getEnumeration("environment_types");
  }

  getMonitoringTypes() {
    return this.getEnumeration("monitoring_types");
  }

  getRoles() {
    return this.getEnumeration("roles");
  }

  getServerTypes() {
    return this.getEnumeration("server_types");
  }

  getUsStates() {
    return this.getEnumeration("us_states");
  }

  getOperatingSystemTypes() {
    return this.getEnumeration("operating_system_types");
  }

  getProjectStatusTypes() {
    return new Promise((resolve) => {
      resolve(PROJECT_STATUS);
    });
  }

  getInstanceTaskConfiguration() {
    return Promise.resolve(TASK_CONFIGURATION);
  }

  getClientStatuses() {
    return this.getEnumeration("client_statuses");
  }

  getPerfmonCounterInstancesV2(clientId: any, category: any, query: any) {
    if (!clientId) return Promise.resolve();
    const url = isNullOrUndefined(category)
      ? `v2/clients/${clientId}/performance-counters/instances`
      : `v2/clients/${clientId}/performance-counters/instances?categoryName=${category}${query}`;
    return super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getPerfmonCategoriesV2(clientId: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/performance-counters`,
        headers: { ...API.getAuthHeaders() },
        query: {},
      })
    );

    return result;
  }

  async getCollectorsV2(clientId: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/collectors`,
        headers: { ...API.getAuthHeaders() },
        query: {},
      })
    );

    return {
      data:
        result?.collectors?.map((i: any) => ({
          ...i,
          display: i.name,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  async getPerfmonAggV2() {
    const response = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/performance-counters/aggregations/types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        response?.types?.map((i: any) => ({
          ...i,
          display: i.name,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  async getOptimizationTypes() {
    const response = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/indexes/optimization-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        response?.types?.map((i: any) => ({
          ...i,
          display: i.name,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  getClientManagementTypes() {
    return this.getEnumeration("client_management_types");
  }

  getClientSubscriptionStatuses() {
    return this.getEnumeration("client_subscription_statuses");
  }

  getPlatforms() {
    return super.call(
      new ApiGetCallParams({
        action: `v2/lookup/platforms`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getServers(clientId: number) {
    if (!clientId) return Promise.resolve();
    return this.getEnumeration("servers_lookup", clientId);
  }

  getServersV2(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/servers`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getHostsV2(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/hosts`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getInstancesV2(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/instances`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getDatabasesV2(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/databases`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getDatabasesV2Q(clientId: number, query: string | undefined) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/databases${query}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getResourcesV2F(clientId: number) {
    if (!clientId) return Promise.resolve();
    const response = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/resources`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        response?.data?.map((i: any) => ({
          id: i.id,
          resourceType: i.type,
          display: `${i.name} (${i.type})`,
          name: `${i.name} (${i.type})`,
          value: `${i.name} (${i.type})`,
        })) || [],
    };
  }

  async getResourcesV2(clientId: number) {
    if (!clientId) return Promise.resolve();
    const response = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/resources`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data:
        response?.data?.map((i: any) => ({
          id: i.id,
          resourceType: i.type,
          display: i.name,
          name: i.name,
          value: i.name,
        })) || [],
    };
  }

  getDatabaseEditionV2() {
    return super.call(
      new ApiGetCallParams({
        action: `v2/lookup/database-editions`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getTags(clientId: number) {
    if (!clientId) return Promise.resolve();
    const response = await super.call(
      new ApiGetCallParams({
        action: `${clientId}/resource-tags`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    const flatTags: any = [];

    response?.tags?.forEach((i: any) => {
      const res: any = i?.values?.map((item: any) => ({
        name: `${i.name}:  ${item.value}`,
        value: `${i.name}:  ${item.value}`,
        accountId: i.accountId,
        tagId: i.tagId,
        allowMultipleValuesForSameObject: i.allowMultipleValuesForSameObject,
      }));

      flatTags.push(...res);
    });

    return { data: flatTags };
  }

  async getTagsV2(clientId: number, tagView: string | null) {
    if (!clientId) return Promise.resolve();
    const url = tagView ? `${clientId}/resource-tags?view=${tagView}` : `${clientId}/resource-tags`;
    const response = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return { data: response?.tags };
  }

  getLoginsByInstanceV2(clientId: string | number, queryParams = null) {
    if (!clientId) return Promise.resolve();
    const url = queryParams
      ? `v2/clients/${clientId}/logins?${queryParams}`
      : `v2/clients/${clientId}/logins`;
    return super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getProgramsByInstanceV2(clientId: string | number, queryParams = null) {
    if (!clientId) return Promise.resolve();
    const url = queryParams
      ? `v2/clients/${clientId}/programs?${queryParams}`
      : `v2/clients/${clientId}/programs`;
    return super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getConnectionHostsV2(clientId: string | number, queryParams = null) {
    if (!clientId) return Promise.resolve();
    const url = queryParams
      ? `v2/clients/${clientId}/connection-hosts?${queryParams}`
      : `v2/clients/${clientId}/connection-hosts`;
    return super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getWaitTypesV2(clientId: string | number, queryParams = null) {
    if (!clientId) return Promise.resolve();
    const url = queryParams
      ? `v2/clients/${clientId}/wait-types?${queryParams}`
      : `v2/clients/${clientId}/wait-types`;
    return super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getWaitCategoriesV2(clientId: string | number, queryParams = null) {
    if (!clientId) return Promise.resolve();
    const url = queryParams
      ? `v2/clients/${clientId}/wait-categories?${queryParams}`
      : `v2/clients/${clientId}/wait-categories`;
    const result = await super.call(
      new ApiGetCallParams({
        action: url,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: result?.data?.map((i: any, idx: number) => ({
        id: idx + 1,
        ...i,
        display: i.name,
        value: i.name,
        values: i.waitTypes.map((t: any) => ({ ...t, value: t.name })),
      })),
    };
  }

  getBlockingIdsV2(clientId: string | number, queryParams: any) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/blockers?${queryParams}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getDeadlockGroupsV2(clientId: string | number, queryParams: any) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/deadlock-groups?${queryParams}`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getLoginNames(clientId: number) {
    if (!clientId) return Promise.resolve();
    return this.getEnumeration("logins", clientId);
  }

  getProgramsNames(clientId: number) {
    if (!clientId) return Promise.resolve();
    return this.getEnumeration("sql_activity_programs", clientId);
  }

  getWaitStats(clientId: number) {
    //return this.getEnumeration("wait_stats");

    if (!clientId) return Promise.resolve();
    return this.getEnumeration("sql_activity_wait_type", clientId);
  }

  getInstanceLoginsList(clientId: number) {
    if (!clientId) return Promise.resolve();
    return this.getEnumeration("instance_logins", clientId);
  }

  getAvailabilityGroupInstancesList(clientId: number) {
    if (!clientId) return Promise.resolve();
    return this.getEnumeration("availability_groups_instances", clientId);
  }

  async getAvailabilityGroupDatabaseList(clientId: number) {
    if (!clientId) return Promise.resolve();
    const response = await this.getEnumeration("availability_groups_instance_databases", clientId);

    const result =
      getUniqueSortedList(response?.data, "databaseName")?.map((i) => ({
        name: i,
        value: i,
      })) || [];

    return {
      data: result,
    };
  }

  async getAuthenticationMethods() {
    try {
      const result = await super.call(
        new ApiGetCallParams({
          action: `v3/lookup/authentication-methods`,
          headers: { ...API.getAuthHeaders() },
        })
      );

      return result || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getConnectionHostsByInstance(clientId: number, instance: string) {
    if (!clientId) return Promise.resolve();
    const result = await this.getEnumeration(
      `connections_instance_hosts_by_instance`,
      clientId,
      {
        instance,
      },
      "host_name"
      //NO_CACHE_MS
    );

    return {
      data: ((result && result.data) || []).map((u: any) => ({
        display: u.host_name,
        name: u.host_name,
      })),
    };
  }

  getQueryString(query: any) {
    let queryString = "";
    Object.keys(query).forEach((key) => {
      queryString += `${key}=${query[key]}&`;
    });
    return queryString === "" ? queryString : `?${queryString}`;
  }

  getTables(clientId: number, instanceName: string, databaseName: string) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/tables:query`,
        body: {
          instanceName,
          databaseName,
        },
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getOpportunitiesOwners(clientId: number) {
    if (!clientId) return Promise.resolve();

    return super.call(
      new ApiGetCallParams({
        action: `dashboards/${clientId}/findings/users?view=AvailableForAssignment`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getFindingOwners(clientId: number) {
    if (!clientId) return Promise.resolve();

    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/opportunities/users`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getImpactTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/impact-categories/types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: ((result && result?.categories) || []).map((i: any) => ({
        display: i.name,
        name: i.name,
        value: i.name,
        id: i.id,
      })),
    };
  }

  async getQueryObservationTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/query-observation-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: ((result && result?.observationTypes) || []).map((i: any) => ({
        display: i.name,
        name: i.name,
        value: i.name,
        id: i.id,
      })),
    };
  }

  async getStatusTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/status-types/assignable`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: ((result && result?.statusTypes) || []).map((i: any) => ({
        display: i.name,
        name: i.name,
        value: i.name,
        id: i.id,
      })),
    };
  }

  getAlertTypes(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `/v2/clients/${clientId}/alerts/types?view=SystemAndServerAlerts`,
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to get alert types",
      })
    );
  }

  getAlertSeverityTypes(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerts/severity-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getAlertCategoryTypes(clientId: number) {
    if (!clientId) return Promise.resolve();
    return super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerts/category-types?view=SystemAndServerAlerts`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  getDataTypes() {
    return super.call(
      new ApiGetCallParams({
        action: `v2/lookup/data-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );
  }

  async getJobsList(clientId: number, startDateTime: string, endDateTime: string) {
    if (!clientId) return Promise.resolve();
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/jobs?endDateTime=${endDateTime}&startDateTime=${startDateTime}&timeZoneOffset=${timeZoneOffset}`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: ((result && result.jobs) || []).map((i: any) => ({
        display: i.name,
        name: i.name,
      })),
    };
  }

  async getJobsCategories(clientId: number, startDateTime: string, endDateTime: string) {
    if (!clientId) return Promise.resolve();
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/job-categories?endDateTime=${endDateTime}&startDateTime=${startDateTime}&timeZoneOffset=${timeZoneOffset}`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return {
      data: ((result && result.jobCategories) || []).map((i: any) => ({
        display: i.name,
        name: i.name,
      })),
    };
  }

  async getBusinessGoalTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/business-goal-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return { data: result?.businessGoalTypes };
  }

  async getEffortTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/effort-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return { data: result?.effortTypes };
  }

  async getOpportunityImpactTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/impact-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return result?.impactTypes;
  }

  async getOpportunityEffortTypes() {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/opportunities/effort-types`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return result?.effortTypes;
  }

  removeCache(cacheName: string, clientId?: number) {
    let cacheKeysToRemove: Nullable<string[]> = null;
    switch (cacheName) {
      case "Hosts":
        cacheKeysToRemove = HostCacheKeys;

        break;

      case "DatabaseInstances":
        cacheKeysToRemove = InstanceCacheKeys;
        break;

      default:
        console.error("unknown cache key", cacheName);
        break;
    }
    if (cacheKeysToRemove !== null) {
      cacheKeysToRemove.forEach((cacheName) => {
        try {
          const cacheKey = `getEnumeration_${cacheName}${clientId || ""}`;
          LookupsMap.delete(cacheKey); //todo: make a request and look for a change?
        } catch (error) {
          console.log("removeCache", error);
        }
      });
    }
  }

  async getTimezones() {
    const response = await super.call(
      new ApiGetCallParams({
        action: `v2/lookup/time-zones`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    const timezones: any = sortTimezonesByUtcOffset(
      response?.timeZones?.map((i: any) => {
        return {
          text: `${i.name} (UTC ${i.currentUtcOffset?.includes("-") ? i.currentUtcOffset : "+" + i.currentUtcOffset})`,
          value: i.name,
          iana: i?.ianaName,
        };
      })
    );

    return {
      data: timezones,
    };
  }

  async getEnumeration(path: string, clientId?: number, query: any = {}, sortByField = "name") {
    const cacheKey = `getEnumeration_${path}${clientId || ""}${this.getQueryString(query)}`;

    //not in progress > set in progress flag- dont allow to calls at the same time. javascript is single threaded so no need to put the flag only if it's no in cache- as 2 functions cannot run together at the same time
    if (!this.inProgressPaths.has(cacheKey) || this.inProgressPaths.get(cacheKey) === false) {
      this.inProgressPaths.set(cacheKey, true);
    }
    //wait until a running request is finished and use it's result
    else {
      while (this.inProgressPaths.get(cacheKey) === true) {
        // a request to the server for this path + clientid already is currently in progress
        // console.log(
        //   this.id,
        //   `a request to the server for path: ${path} and clientId: ${clientId}  is already in progress`
        // );
        await sleep(50); //todo change to events?
      }
    }

    // console.log(`${this.id} ${cacheKey} is not in cache. fetching from server ... `);

    const cached = LookupsMap.get(cacheKey); //  getLocalStorageWithExpiry(cacheKey);

    if (cached) {
      this.inProgressPaths.set(cacheKey, false);
      return cached;
    }

    let action: string;
    if (clientId) {
      action = `clients/${clientId}/${path}`;
    } else {
      action = `lookup/${path}`;
    }

    try {
      const result = await super.call(
        new ApiGetCallParams({
          action,
          headers: { ...API.getAuthHeaders() },
          query,
        })
      );

      if (result && result.data && result.data.length && !result.isAborted) {
        result.data
          .sort((a: any, b: any) =>
            !a[sortByField] ? 1 : a[sortByField].localeCompare(b[sortByField])
          )
          .forEach((d: any) => {
            if (d.name) d.name = d.name.trim();
          });

        LookupsMap.set(cacheKey, result);
      }
      this.inProgressPaths.set(cacheKey, false);

      return result || null;
    } catch (error) {
      console.error(error);
      this.inProgressPaths.set(cacheKey, false);
      return null;
    }
  }
}
const instance = new LookupService();
export default instance;
